import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import Paper from '@base/Paper/Paper';
import Button from '@base/Button/Button';
import {
  getCurrentSessionUID,
  getCurrentQuestionInfo,
  getQuizQuestionAnswers,
  getCurrentSelectedAnswer,
  getCurrentQuestion,
  getIsPendingAction,
  getCurrentQuizType,
  getQuizDetails
} from '@selectors/quizTaking';
import {
  toggleMarkQuestion,
  submitAnswerResponse,
  setCurrentSelectedAnswer
} from '@actions/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { Spacings } from '@styles/index';
import { arrayAreEqual } from '@utils/equalFns';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import useQuestionPagination from '@cont/Quiz/Taking/Common/useQuestionPagination';
import useQuestionStatus from '@cont/Quiz/Taking/Common/useQuestionStatus';
import useFailedAPIQueue from '@cont/Quiz/Taking/Common/useFailedAPIQueue';
import boxShadowGenerator from '@utils/boxShadowGenerator';

const Flex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Wrapper = styled(Paper)`
  position: sticky;
  bottom: 0;
  border-bottom-right-radius: ${Spacings.SPACING_0B};
  border-bottom-left-radius: ${Spacings.SPACING_0B};
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator({ yOffset: '-16px' })};
`;

const MarkButton = styled(Button)`
  margin-right: ${Spacings.SPACING_4B};
`;

const QuestionActions = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const currentQuestion = useSelectorWithShallowEqual(getCurrentQuestion);
  const { sectionUID, questionUID } = useSelectorWithShallowEqual(
    getCurrentQuestionInfo
  );
  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);

  const answers = useSelectorWithShallowEqual(
    getQuizQuestionAnswers(sectionUID, questionUID)
  );
  const currentSelectedAnswer = useSelectorWithShallowEqual(
    getCurrentSelectedAnswer
  );

  const { addFailedSubmitAPI } = useFailedAPIQueue();
  const { isAttempted, isMarked } = useQuestionStatus(sectionUID, questionUID);
  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);

  const { handleOnNext } = useQuestionPagination();
  const { open: showSnackbar } = useSnackbar();

  const handleOnBookmark = () => {
    dispatch(
      toggleMarkQuestion(
        quizUID,
        currentSessionUID,
        sectionUID,
        questionUID,
        !isMarked
      )
    ).then((resp) => {
      if (resp.error) {
        showSnackbar({
          message: `Unable to bookmark question ${currentQuestion.question_rank}. Please try again`,
          type: 'error'
        });
      }
    });
  };

  const getSaveBtnVariant = () => {
    if (!isAttempted && !isQuestionAttemptable) {
      return 'disabled';
    }
    return 'filled';
  };

  const handleOnSave = () => {
    if (!arrayAreEqual(answers, currentSelectedAnswer)) {
      const data = {
        question_uid: questionUID,
        attempt_list: currentSelectedAnswer
      };

      dispatch(
        submitAnswerResponse(quizUID, sectionUID, currentSessionUID, data)
      ).then((resp) => {
        if (resp.error) {
          addFailedSubmitAPI(data);
        }
      });
    }
    if (currentQuestion.question_rank !== quizDetails.num_questions) {
      handleOnNext();
    }
  };

  const handleClearResponse = () => {
    const data = {
      question_uid: questionUID,
      attempt_list: []
    };
    if (isAttempted) {
      dispatch(
        submitAnswerResponse(quizUID, sectionUID, currentSessionUID, data)
      ).then((resp) => {
        if (resp.error) {
          addFailedSubmitAPI(data);
        } else {
          dispatch(setCurrentSelectedAnswer([]));
        }
      });
    } else {
      return dispatch(setCurrentSelectedAnswer([]));
    }
  };

  const getClearResponseVariant = () => {
    if (currentSelectedAnswer.length) {
      return 'hollow';
    }
    return 'disabled';
  };

  const getSaveBtnLabel = () => {
    if (currentQuestion.question_rank === quizDetails.num_questions) {
      return 'Save';
    }
    return 'Save & next';
  };

  return (
    <Wrapper>
      <Flex>
        <Flex>
          <MarkButton
            grid={3}
            variant={isPendingAction ? 'disabled' : 'hollow'}
            width="164px"
            color="black"
            label={isMarked ? 'Unmark question' : 'Mark for review'}
            onClick={handleOnBookmark}
          />
          {!isSubjective && (
            <Button
              grid={3}
              width="164px"
              variant={getClearResponseVariant()}
              color="black"
              label="Clear response"
              onClick={handleClearResponse}
            />
          )}
        </Flex>

        <Flex>
          <Button
            grid={4}
            width="164px"
            label={getSaveBtnLabel()}
            variant={getSaveBtnVariant()}
            onClick={handleOnSave}
          />
        </Flex>
      </Flex>
    </Wrapper>
  );
};

export default QuestionActions;
